import { NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';

import { Store } from '@ngrx/store';

import { OrderConfirmChangeCaComponent } from '@components/orders/manage-orders/production-flow/order-production-details/order-confirm-change-ca/order-confirm-change-ca.component';
import { Subscription } from 'rxjs';

import { selectOrderDetails } from 'src/@store/orders/orders.selectors';

import { ConstructionStepPayload } from '../../../../../@store/orders/orders.model';
import { selectIsOrdersManager } from '../../../../../@store/user/user.selectors';
import { SafeAny } from '../../../../../types/safe-any';

@Component({
  selector: 'ciwc-order-construction-steps',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    NgIf,
    NgFor,
    MatTooltipModule,
  ],
  standalone: true,
  templateUrl: './order-construction-steps.component.html',
  styleUrl: './order-construction-steps.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderConstructionStepsComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  readonly isOrdersManager = this.store.selectSignal(selectIsOrdersManager);
  readonly selectedOrder = this.store.selectSignal(selectOrderDetails);

  @Input() parentForm!: FormGroup;
  private _specification: Record<string, unknown> | SafeAny = {};
  @Input() set specification(value: Record<string, unknown> | SafeAny) {
    const prevSpec = this._specification;
    this._specification = value || {};
    if (prevSpec && this._specification && Object.keys(prevSpec).length > 0) {
      this.syncSelectionsWithSpecification(prevSpec, this._specification);
    }
  }
  get specification(): Record<string, unknown> | SafeAny {
    return this._specification || {};
  }
  @Input() viewDisable!: boolean;
  @Input() readonly = false;
  @Output() constructionStepsChange: EventEmitter<ConstructionStepPayload[]> = new EventEmitter<
    ConstructionStepPayload[]
  >();

  private readonly dialog = inject(MatDialog);
  private orderSub?: Subscription;
  private formSubs: Subscription = new Subscription();

  order: SafeAny = null;

  specFields = [
    { key: 'awg', label: 'AWG' },
    { key: 'stranding', label: 'Stranding' },
    { key: 'wireType', label: 'Wire Type' },
    { key: 'supplier', label: 'Supplier' },
    { key: 'finishedDiameter', label: 'Finished Diameter' },
    { key: 'tip', label: 'Tip' },
    { key: 'die', label: 'Die' },
    { key: 'putUp', label: 'Put Up' },
    { key: 'jacketMaterial', label: 'Jacket Material' },
    { key: 'jacketColor', label: 'Jacket Color' },
    { key: 'jacketPrinting', label: 'Jacket Printing' },
    { key: 'wallThickness', label: 'Wall Thickness' },
    { key: 'innerDiameter', label: 'Inner Diameter' },
    { key: 'outerDiameter', label: 'Outer Diameter' },
  ];

  stepsFormArray: FormArray = this.fb.array([]);

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
  ) {
    effect(() => {
      this.order = this.selectedOrder();

      if (this.order) {
        this.initFormArray(this.order.constructionSteps);
      }
    });
  }

  ngOnInit() {
    this.stepsFormArray.valueChanges.subscribe(() => {
      this.emitSteps();
    });

    // Re-render when parent form fields used by extra checkboxes change
    const clientPartCtrl = this.parentForm?.get('clientPartNo');
    if (clientPartCtrl) {
      this.formSubs.add(
        clientPartCtrl.valueChanges.subscribe((value) => {
          const constructionStepsCtrl = this.parentForm?.get('constructionSteps');

          if (constructionStepsCtrl && Array.isArray(constructionStepsCtrl.value)) {
            const newClienPartNo = value;
            const updatedSteps = constructionStepsCtrl.value.map((step: any) => ({
              ...step,
              clientPartNo: step.clientPartNo ? newClienPartNo : step.clientPartNo,
            }));
            constructionStepsCtrl.setValue(updatedSteps, { emitEvent: false });
          }

          this.cdr.markForCheck();
        }),
      );
    }
    const feetPerReelCtrl = this.parentForm?.get('feetPerReel');
    if (feetPerReelCtrl) {
      this.formSubs.add(
        feetPerReelCtrl.valueChanges.subscribe((value) => {
          const constructionStepsCtrl = this.parentForm?.get('constructionSteps');

          if (constructionStepsCtrl && Array.isArray(constructionStepsCtrl.value)) {
            const newFeetPerReel = value;
            const updatedSteps = constructionStepsCtrl.value.map((step: any) => ({
              ...step,
              feetPerReel: step.feetPerReel ? newFeetPerReel : step.feetPerReel,
            }));
            constructionStepsCtrl.setValue(updatedSteps, { emitEvent: false });
          }

          this.cdr.markForCheck();
        }),
      );
    }
  }

  ngOnDestroy() {
    this.orderSub?.unsubscribe();
    this.formSubs.unsubscribe();
  }

  private syncSelectionsWithSpecification(
    previousSpec: Record<string, unknown>,
    nextSpec: Record<string, unknown>,
  ) {
    // For each step and each selectable field, remap previously selected values
    // to the new specification values (by index) or preserve them if still valid
    for (let i = 0; i < this.stepsFormArray.length; i++) {
      const group = this.getStepGroup(i);
      let updated = false;

      this.specFields.forEach((field) => {
        const control = group.get(field.key) as FormControl<string[]> | null;
        if (!control) {
          return;
        }
        const selected: string[] = Array.isArray(control.value) ? [...control.value] : [];
        const prevList = Array.isArray(previousSpec?.[field.key])
          ? (previousSpec[field.key] as string[])
          : [];
        const nextList = Array.isArray(nextSpec?.[field.key])
          ? (nextSpec[field.key] as string[])
          : [];

        const mapped: string[] = [];
        for (const value of selected) {
          if (typeof value !== 'string') {
            continue;
          }
          // Keep if still present in the new list
          if (nextList.includes(value)) {
            mapped.push(value);
            continue;
          }
          // If it was present in previous list, try to remap by index
          const prevIndex = prevList.indexOf(value);
          if (prevIndex > -1) {
            const replacement = nextList[prevIndex];
            if (typeof replacement === 'string' && replacement.length > 0) {
              mapped.push(replacement);
            }
            continue;
          }
          // Not present anymore; drop it
        }

        // Only set if changed, to avoid extra emissions
        const changed =
          selected.length !== mapped.length || selected.some((v, idx) => v !== mapped[idx]);
        if (changed) {
          control.setValue(mapped);
          updated = true;
        }
      });

      if (updated) {
        // Emit consolidated change once per step-group update cycle
        this.emitSteps();
      }
    }
  }

  initFormArray(constructionSteps: ConstructionStepPayload[]) {
    this.stepsFormArray.clear();
    (constructionSteps || []).forEach((step) => {
      this.stepsFormArray.push(this.createStepGroup(step));
    });
  }

  createStepGroup(
    step?: Partial<ConstructionStepPayload>,
  ): FormGroup<Record<string, FormControl<string | string[] | null>>> {
    const group: Record<string, FormControl<string | string[] | null>> = {
      description: new FormControl({ value: step?.description || '', disabled: this.viewDisable }),
      memo: new FormControl({ value: step?.memo || null, disabled: this.viewDisable }),
    };
    // Extra fields coming from Order Custom Job form (scalar, not arrays)
    group['clientPartNo'] = new FormControl({
      value: (step as SafeAny)?.['clientPartNo'] ?? '',
      disabled: this.viewDisable,
    });
    group['feetPerReel'] = new FormControl({
      value: (step as SafeAny)?.['feetPerReel'] ?? '',
      disabled: this.viewDisable,
    });
    this.specFields.forEach((field) => {
      const fieldValue = step?.[field.key as keyof ConstructionStepPayload];
      group[field.key] = new FormControl({
        value: Array.isArray(fieldValue) ? [...fieldValue] : [],
        disabled: this.viewDisable,
      });
    });
    return this.fb.group(group);
  }

  emitSteps() {
    const steps = this.stepsFormArray.getRawValue() as ConstructionStepPayload[];
    this.constructionStepsChange.emit(steps);
  }

  isChecked(stepGroup: FormGroup, field: string, value: string, index?: number): boolean {
    const current = stepGroup.get(field)?.value;
    // if (field === 'clientPartNo' || field === 'feetPerReel') {
    //   console.log('🚀 ~ current:', current);
    // }
    if (Array.isArray(current)) {
      return current.includes(value);
    }

    // For scalar fields (clientPartNo, feetPerReel), check if the control has a non-empty value
    if (field === 'clientPartNo' || field === 'feetPerReel') {
      const parentValue = this.getParentFieldValue(field);

      return current ? value === parentValue : false;
    }

    return current === value && value !== '' && value !== null;
  }

  onCheckboxChange(stepGroup: FormGroup, field: string, value: string, checked: boolean) {
    if (this.isOrdersManager()) return;

    const current = stepGroup.get(field)?.value;
    if (Array.isArray(current)) {
      const arr = current || [];
      if (checked) {
        if (!arr.includes(value)) {
          arr.push(value);
        }
      } else {
        const idx = arr.indexOf(value);
        if (idx > -1) arr.splice(idx, 1);
      }
      stepGroup.get(field)?.setValue([...arr]);
    } else {
      // For scalar fields (clientPartNo, feetPerReel), use the current parent form value
      if (field === 'clientPartNo' || field === 'feetPerReel') {
        const parentValue = this.getParentFieldValue(field);
        stepGroup.get(field)?.setValue(checked ? parentValue : '');
      } else {
        // Other scalar fields: set to value when checked, null when unchecked
        stepGroup.get(field)?.setValue(checked ? value : null);
      }
    }
    this.emitSteps();
  }

  addStep() {
    this.stepsFormArray.push(this.createStepGroup());
    this.emitSteps();
  }

  removeStep(index: number) {
    if (index > 0) {
      this.stepsFormArray.removeAt(index);
      this.emitSteps();
    }
  }

  getStepGroup(i: number): FormGroup<Record<string, FormControl<string | string[] | null>>> {
    return this.stepsFormArray.at(i) as FormGroup<
      Record<string, FormControl<string | string[] | null>>
    >;
  }

  trackByIndex(index: number, _: unknown): number {
    return index;
  }

  trackByFieldKey(index: number, field: { key: string }): string {
    return field.key;
  }

  trackByValue(index: number, value: unknown): number | unknown {
    return value ?? index;
  }

  isStringArray(value: unknown): value is string[] {
    return Array.isArray(value) && value.every((v) => typeof v === 'string');
  }

  getStringArray(step: Record<string, unknown>, key: string): string[] {
    const val = step[key];
    return Array.isArray(val) && val.every((v) => typeof v === 'string') ? val : [];
  }

  /**
   * Read a scalar value from the parent form for fields that are not part of
   * the CA specification, and normalize to string. Used to drive the extra
   * checkboxes (Client Part # and Feet Per Reel).
   */
  getParentFieldValue(field: 'clientPartNo' | 'feetPerReel'): string {
    const control = this.parentForm?.get(field);
    const raw = control ? control.value : '';
    return raw === null || raw === undefined ? '' : String(raw);
  }

  getFieldDisplayValue(stepGroup: FormGroup, field: 'clientPartNo' | 'feetPerReel'): string {
    if (this.viewDisable) {
      // Get the step index
      const index = this.stepsFormArray.controls.indexOf(stepGroup);
      // Get the value from the original order's construction steps
      const value = this.order?.constructionSteps?.[index]?.[field];
      return value === null || value === undefined ? '' : String(value);
    }
    return this.getParentFieldValue(field);
  }

  onAddStepClick(event: Event) {
    if (this.readonly) {
      this.onReadonlyClick(event);
      return;
    }
    this.addStep();
  }

  onRemoveStepClick(event: Event, index: number) {
    if (this.readonly) {
      this.onReadonlyClick(event);
      return;
    }
    this.removeStep(index);
  }

  onReadonlyClick(event: Event) {
    if (this.isOrdersManager()) return;

    if (this.readonly) {
      event.preventDefault();
      event.stopPropagation();

      this.dialog
        .open(OrderConfirmChangeCaComponent, {
          data: {
            caCustomDescription: this.parentForm.get('caCustomDescription')?.value,
          },
        })
        .afterClosed()
        .subscribe((result: boolean): void => {
          if (result) {
            // User confirmed the change, reset the CA number and custom description
            this.parentForm.get('caNumbersId')?.patchValue(null);
            this.parentForm.get('caCustomDescription')?.patchValue('');
            // Make sure to mark the form for check to update the UI
            this.cdr.markForCheck();
          }
        });
    }
  }

  private mapConstructionFieldsSteps(
    step: ConstructionStepPayload,
    spec: Record<string, unknown>,
  ): Partial<ConstructionStepPayload> {
    const result: Partial<ConstructionStepPayload> = {
      description: step.description || '',
      memo: step.memo || '',
      // Handle scalar fields
      clientPartNo: step.clientPartNo || '',
      feetPerReel: step.feetPerReel || '',
    };

    // Handle array fields
    const fields = [
      'awg',
      'stranding',
      'wireType',
      'supplier',
      'finishedDiameter',
      'tip',
      'die',
      'putUp',
      'jacketMaterial',
      'jacketColor',
      'jacketPrinting',
      'wallThickness',
      'innerDiameter',
      'outerDiameter',
    ] as const;

    for (const field of fields) {
      const stepValue = step[field];
      const specValue = spec[field] as string[] | undefined;

      if (Array.isArray(stepValue)) {
        // Filter out values not in the specification
        const filteredValues = stepValue.filter((val) => specValue?.includes(val));
        // Reorder the values to match the order in the specification
        const orderedValues = specValue?.filter((v) => filteredValues.includes(v));
        result[field] = orderedValues || [];
      } else {
        result[field] = [];
      }
    }

    return result;
  }
}
